/**
 * Composant pour créer et gérer les catégories d'inscription d'un événement.
 * Chaque catégorie définit : nom, taille d'équipe fixe, prix, nombre de places.
 */
import React, { useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  GripVertical,
  Users,
  Euro,
  Calendar,
  Check,
  Circle
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ConfirmDialog from './ui/ConfirmDialog';
import PriceQuote, { CommissionPayer } from './PriceQuote';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { NumberInput } from './ui/number-input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export interface RegistrationCategory {
  id: string;
  name: string;
  description?: string;
  team_size: number;
  price_amount: number;
  price_currency: string;
  max_spots?: number;
  is_active: boolean;
  stripe_price_id?: string;
  display_order: number;
  current_registrations?: number;
}

interface RegistrationCategoryBuilderProps {
  categories: RegistrationCategory[];
  onChange: (categories: RegistrationCategory[]) => void;
  /** Sans eventId, pas d'aperçu de ce que l'organisateur reçoit. */
  eventId?: string;
  commissionPayer?: CommissionPayer;
}

const RegistrationCategoryBuilder: React.FC<RegistrationCategoryBuilderProps> = ({
  categories,
  onChange,
  eventId,
  commissionPayer = 'athlete',
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<RegistrationCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    team_size: 1,
    price_amount: 0,
    price_currency: 'EUR',
    max_spots: null as number | null,
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleOpenDialog = (category?: RegistrationCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        team_size: category.team_size,
        price_amount: category.price_amount / 100, // Convertir centimes → euros
        price_currency: category.price_currency,
        max_spots: category.max_spots || null,
        is_active: category.is_active,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        team_size: 1,
        price_amount: 0,
        price_currency: 'EUR',
        max_spots: null,
        is_active: true,
      });
    }
    setErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    if (formData.team_size < 1) {
      newErrors.team_size = 'Taille d\'équipe minimum : 1';
    }
    if (formData.price_amount < 0) {
      newErrors.price_amount = 'Le prix ne peut pas être négatif';
    }
    if (formData.max_spots && formData.max_spots < 1) {
      newErrors.max_spots = 'Nombre de places minimum : 1';
    }

    // Vérifier unicité du nom (sauf si on édite la même catégorie)
    const nameExists = categories.some(
      cat => cat.name.toLowerCase() === formData.name.trim().toLowerCase() &&
             cat.id !== editingCategory?.id
    );
    if (nameExists) {
      newErrors.name = 'Une catégorie avec ce nom existe déjà';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveCategory = () => {
    if (!validateForm()) return;

    const newCategory: RegistrationCategory = {
      id: editingCategory?.id || uuidv4(),
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      team_size: formData.team_size,
      price_amount: Math.round(formData.price_amount * 100), // Convertir euros → centimes
      price_currency: formData.price_currency,
      max_spots: formData.max_spots || undefined,
      is_active: formData.is_active,
      display_order: editingCategory?.display_order ?? categories.length,
      current_registrations: editingCategory?.current_registrations || 0,
    };

    if (editingCategory) {
      // Mise à jour
      onChange(categories.map(c => (c.id === editingCategory.id ? newCategory : c)));
    } else {
      // Ajout
      onChange([...categories, newCategory]);
    }

    handleCloseDialog();
  };

  const handleDeleteCategory = (id: string) => {
    const category = categories.find(c => c.id === id);
    if (category && category.current_registrations && category.current_registrations > 0) {
      toast.error(t('registrations.categoryBuilder.cannotDelete', { count: category.current_registrations }));
      return;
    }
    setCategoryToDelete(id);
  };

  const confirmDeleteCategory = () => {
    if (!categoryToDelete) return;
    onChange(categories.filter(c => c.id !== categoryToDelete));
    setCategoryToDelete(null);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const newCategories = [...categories];
    const [moved] = newCategories.splice(from, 1);
    newCategories.splice(to, 0, moved);
    newCategories.forEach((c, i) => (c.display_order = i));
    onChange(newCategories);
  };

  const handleToggleActive = (id: string) => {
    onChange(
      categories.map(c =>
        c.id === id ? { ...c, is_active: !c.is_active } : c
      )
    );
  };

  const formatPrice = (amountCents: number, currency: string): string => {
    const symbols: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF' };
    const amount = (amountCents / 100).toFixed(2);
    return `${amount} ${symbols[currency] || currency}`;
  };

  const getTeamSizeLabel = (size: number): string => {
    if (size === 1) return 'Solo (1 participant)';
    if (size === 2) return 'Duo (2 participants)';
    if (size === 3) return 'Trio (3 participants)';
    return `Équipe de ${size}`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Catégories d'inscription</h3>
        <Button variant="outline" onClick={() => handleOpenDialog()}>
          <Plus size={18} className="mr-2" />
          Ajouter une catégorie
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground mb-2">
              Aucune catégorie d'inscription. Ajoutes-en pour permettre les inscriptions à ton événement.
            </p>
            <p className="text-xs text-muted-foreground">
              Ex: "RX Solo" (1 personne, 50€), "RX Duo" (2 personnes, 90€), "Scaled Solo" (1 personne, 40€)
            </p>
          </CardContent>
        </Card>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="registration-categories">
            {(provided) => (
              <div className="space-y-2" ref={provided.innerRef} {...provided.droppableProps}>
                {categories.map((category, index) => (
                  <Draggable key={category.id} draggableId={category.id} index={index}>
                    {(provided, snapshot) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`${!category.is_active ? 'opacity-60 border-dashed border-2' : ''} ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div
                              {...provided.dragHandleProps}
                              className="mt-1 text-muted-foreground cursor-grab active:cursor-grabbing"
                            >
                              <GripVertical size={20} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold">{category.name}</span>
                                  {!category.is_active && (
                                    <Badge variant="secondary">Désactivée</Badge>
                                  )}
                                  {category.current_registrations && category.current_registrations > 0 && (
                                    <Badge className="bg-success/20 text-success border-success/40">
                                      {category.current_registrations} inscription(s)
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="gap-1">
                                    <Users size={14} />
                                    {getTeamSizeLabel(category.team_size)}
                                  </Badge>
                                  <Badge variant="outline" className="gap-1">
                                    <Euro size={14} />
                                    {formatPrice(category.price_amount, category.price_currency)}
                                  </Badge>
                                  {category.max_spots && (
                                    <Badge variant="outline" className="gap-1">
                                      <Calendar size={14} />
                                      Max: {category.max_spots} places
                                    </Badge>
                                  )}
                                </div>

                                <div className="flex gap-1 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleActive(category.id)}
                                    className={`h-8 w-8 p-0 ${category.is_active ? 'text-success' : 'text-muted-foreground'}`}
                                    title={category.is_active ? 'Désactiver' : 'Activer'}
                                  >
                                    {category.is_active ? <Check size={18} /> : <Circle size={18} />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenDialog(category)}
                                    className="h-8 w-8 p-0 text-muted-foreground"
                                  >
                                    <Edit size={18} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCategory(category.id)}
                                    disabled={!!(category.current_registrations && category.current_registrations > 0)}
                                    className="h-8 w-8 p-0 text-destructive"
                                  >
                                    <Trash2 size={18} />
                                  </Button>
                                </div>
                              </div>

                              {category.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-line">
                                  {category.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Dialog de configuration */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la catégorie *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder='Ex: "RX Solo", "RX Duo"'
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              <p className="text-xs text-muted-foreground">
                Ex: "RX Solo", "RX Duo", "Scaled Team"
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                placeholder="Description visible sur le formulaire"
              />
              <p className="text-xs text-muted-foreground">
                Description visible sur le formulaire d'inscription (optionnel)
              </p>
            </div>

            {/* La capacité compte des inscriptions : des équipes, ou des athlètes en solo. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="team_size">Taille d'équipe FIXE *</Label>
                <NumberInput
                  id="team_size"
                  min={1}
                  max={10}
                  value={formData.team_size}
                  onChange={e => setFormData({ ...formData, team_size: parseInt(e.target.value) || 1 })}
                />
                {errors.team_size && <p className="text-sm text-destructive">{errors.team_size}</p>}
                <p className="text-xs text-muted-foreground">
                  1 = Solo, 2 = Duo, 3 = Trio, etc.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_spots">{formData.team_size > 1 ? "Nombre d'équipes maximum" : "Nombre d'athlètes maximum"}</Label>
                <NumberInput
                  id="max_spots"
                  min={1}
                  value={formData.max_spots || ''}
                  onChange={e => setFormData({ ...formData, max_spots: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Illimité"
                />
                {errors.max_spots && <p className="text-sm text-destructive">{errors.max_spots}</p>}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="price_amount">{formData.team_size > 1 ? 'Prix par équipe *' : 'Prix par athlète *'}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {formData.price_currency === 'EUR' ? '€' : formData.price_currency}
                  </span>
                  <NumberInput
                    id="price_amount"
                    step="0.01"
                    min={0}
                    value={formData.price_amount}
                    onChange={e => setFormData({ ...formData, price_amount: parseFloat(e.target.value) || 0 })}
                    className="pl-8"
                  />
                </div>
                {formData.team_size > 1 && (
                  <p className="text-xs text-muted-foreground">{t('registrations.categoryBuilder.onePaymentPerTeam')}</p>
                )}
                {errors.price_amount && <p className="text-sm text-destructive">{errors.price_amount}</p>}
              </div>

              <div className="space-y-2">
                <Label>Devise</Label>
                <Select
                  value={formData.price_currency}
                  onValueChange={(value) => setFormData({ ...formData, price_currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                    <SelectItem value="CHF">CHF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {eventId && formData.price_currency === 'EUR' && (
              <PriceQuote
                eventId={eventId}
                payer={commissionPayer}
                price={formData.price_amount}
                onUsePrice={(prix) => setFormData(f => ({ ...f, price_amount: prix }))}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Annuler
            </Button>
            <Button onClick={handleSaveCategory} disabled={!formData.name.trim()}>
              {editingCategory ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={categoryToDelete !== null}
        onOpenChange={(o) => { if (!o) setCategoryToDelete(null); }}
        title={t('registrations.categoryBuilder.deleteTitle')}
        message={t('registrations.categoryBuilder.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={confirmDeleteCategory}
      />
    </div>
  );
};

export default RegistrationCategoryBuilder;
